import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import Box from '../../components/Common/Box';
import Header from '../../components/Header/Header';
import { useAuth } from '../../context/AuthContext';
import { LiveCours, LiveSeance, LiveSerie, useLiveMarket } from '../../hooks/useLiveMarket';
import SiteLayout from '../../layouts/SiteLayout';
import LiveMarketPeriode from './LiveMarketPeriode';

/**
 * Marché live BRVM — écran d'administration.
 *
 * ⚠️ Cet écran n'affiche PAS des données officielles. Il présente un
 * échantillonnage, toutes les 15 minutes, de la page publique des cours. Les
 * extrêmes sont des minorants de l'amplitude réelle de la séance, et l'écran
 * le dit explicitement — bandeau d'avertissement et colonne « Obs. » — parce
 * qu'une valeur cohérente n'est pas une valeur prouvée.
 */

const COULEUR_HAUSSE = '#03543f';
const COULEUR_BAISSE = '#9b1c1c';

const fmtNombre = (v: number | null | undefined, decimales = 0): string =>
    v === null || v === undefined
        ? '—'
        : v.toLocaleString('fr-FR', {
              minimumFractionDigits: decimales,
              maximumFractionDigits: decimales,
          });

const fmtMontantCourt = (v: number | null | undefined): string => {
    if (v === null || v === undefined) return '—';
    if (Math.abs(v) >= 1e12) return `${(v / 1e12).toFixed(2)} Bn F`;
    if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(2)} Md F`;
    if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)} M F`;
    return `${fmtNombre(v)} F`;
};

const fmtHeure = (iso: string | null): string =>
    iso ? iso.slice(11, 16) : '—';

const fmtHorodatage = (iso: string | null): string =>
    iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} à ${iso.slice(11, 16)}` : '—';

const badge = (fond: string, texte: string, bordure: string): React.CSSProperties => ({
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: fond,
    color: texte,
    border: `1px solid ${bordure}`,
    whiteSpace: 'nowrap',
});

const STYLE_BADGE: Record<string, React.CSSProperties> = {
    OK: badge('#def7ec', '#03543f', '#31c48d'),
    PERIME: badge('#fef3c7', '#92400e', '#fcd34d'),
    ERREUR: badge('#fde2e2', '#9b1c1c', '#f8b4b4'),
    NEUTRE: badge('#f1f5f9', '#475569', '#cbd5e1'),
};

const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

const fmtDuree = (minutes: number): string => {
    if (minutes < 60) return `${Math.max(minutes, 1)} min`;
    if (minutes >= 48 * 60) {
        const j = Math.floor(minutes / 1440);
        const reste = Math.floor((minutes % 1440) / 60);
        return reste === 0 ? `${j} j` : `${j} j ${reste} h`;
    }
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')}`;
};

/** « lundi 05/10 à 09:00 », ou « 09:45 » si l'échéance tombe le jour même. */
const fmtEcheance = (iso: string, memeJour: boolean): string => {
    const heure = iso.slice(11, 16);
    if (memeJour) return heure;
    // Midi UTC : le jour de la semaine ne dépend pas du fuseau du navigateur.
    const jour = JOURS[new Date(`${iso.slice(0, 10)}T12:00:00Z`).getUTCDay()];
    return `${jour} ${iso.slice(8, 10)}/${iso.slice(5, 7)} à ${heure}`;
};

/** Ce que la phase implique pour qui regarde l'écran : des cours peuvent-ils bouger ? */
const CONSEQUENCE_PHASE: Partial<Record<LiveSeance['phase'], string>> = {
    PRE_OUVERTURE: 'Les ordres s’accumulent ; aucun échange avant le fixing d’ouverture.',
    CONTINU: 'Les ordres s’exécutent au fil de l’eau.',
    PRE_CLOTURE: 'Les ordres s’accumulent ; aucun échange avant le fixing de clôture.',
    DERNIER_COURS: 'Échanges au cours de clôture uniquement : le cours ne bouge plus.',
};

const BandeauSeance: React.FC<{ seance: LiveSeance }> = ({ seance }) => {
    const style = !seance.ouvert ? STYLE_BADGE.NEUTRE : seance.echanges_possibles ? STYLE_BADGE.OK : STYLE_BADGE.PERIME;
    const memeJour = seance.changement_at?.slice(0, 10) === seance.heure_marche.slice(0, 10);
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <span style={{ ...style, fontSize: '13px', padding: '6px 14px' }}>
                {seance.ouvert ? 'MARCHÉ OUVERT' : 'MARCHÉ FERMÉ'}
            </span>
            <strong style={{ fontSize: '14px' }}>{seance.ouvert ? seance.libelle : seance.libelle.replace(/^Fermé — /, '')}</strong>
            {seance.changement_at && seance.minutes_avant_changement !== null && (
                <span style={{ fontSize: '13px', color: '#475569' }}>
                    {seance.ouvert ? 'Fin de phase à ' : memeJour ? 'Pré-ouverture à ' : 'Pré-ouverture '}
                    {fmtEcheance(seance.changement_at, seance.ouvert || memeJour)}
                    {' · dans '}
                    {fmtDuree(seance.minutes_avant_changement)}
                </span>
            )}
            {CONSEQUENCE_PHASE[seance.phase] && (
                <span style={{ fontSize: '12px', color: '#64748b', flexBasis: '100%' }}>
                    {CONSEQUENCE_PHASE[seance.phase]}
                </span>
            )}
            <span style={{ fontSize: '12px', color: '#94a3b8', flexBasis: '100%' }}>
                Heure du marché : {seance.heure_marche.slice(11, 16)} UTC (Abidjan). Séance : pré-ouverture 09:00, fixing
                09:45, continu jusqu’à 14:00, fixing de clôture 14:30, fermeture 15:00. Les séances écourtées de veille
                de fête, annoncées par avis, ne sont pas prises en compte.
            </span>
        </div>
    );
};

const th: React.CSSProperties = {
    color: 'white',
    padding: '12px 14px',
    textAlign: 'right',
    fontSize: '12px',
    whiteSpace: 'nowrap',
};
const thGauche: React.CSSProperties = { ...th, textAlign: 'left' };
const td: React.CSSProperties = { padding: '10px 14px', textAlign: 'right', fontSize: '13px' };
const tdGauche: React.CSSProperties = { ...td, textAlign: 'left' };

type CleTri =
    | 'societe_symbole'
    | 'nom_affiche'
    | 'volume'
    | 'cours_veille'
    | 'cours_ouverture'
    | 'cours_dernier'
    | 'variation_pct'
    | 'plus_haut_observe'
    | 'plus_bas_observe'
    | 'nb_observations';

interface ColonneTableau {
    cle: CleTri;
    libelle: string;
    gauche?: boolean;
}

const COLONNES: ColonneTableau[] = [
    { cle: 'societe_symbole', libelle: 'Symbole', gauche: true },
    { cle: 'nom_affiche', libelle: 'Nom', gauche: true },
    { cle: 'volume', libelle: 'Volume' },
    { cle: 'cours_veille', libelle: 'Veille' },
    { cle: 'cours_ouverture', libelle: 'Ouverture' },
    { cle: 'cours_dernier', libelle: 'Dernier' },
    { cle: 'variation_pct', libelle: 'Var.' },
    { cle: 'plus_haut_observe', libelle: 'Haut obs.' },
    { cle: 'plus_bas_observe', libelle: 'Bas obs.' },
    { cle: 'nb_observations', libelle: 'Obs.' },
];

/** Colonnes textuelles : premier clic en ordre croissant. Les autres : décroissant d'abord (le plus actif en tête). */
const COLONNES_TEXTE: ReadonlySet<CleTri> = new Set<CleTri>(['societe_symbole', 'nom_affiche']);

const AdminLiveMarketScreen: React.FC = () => {
    const { hasPermission } = useAuth();
    const {
        run, activite, cours, ohlc, runs, meta,
        loading, error, captureEnCours, capturer, chargerSerie,
    } = useLiveMarket();

    const [onglet, setOnglet] = useState<'seance' | 'periode'>('seance');
    const [selection, setSelection] = useState<string | null>(null);
    const [serie, setSerie] = useState<LiveSerie | null>(null);
    const [journalOuvert, setJournalOuvert] = useState(false);
    const [messageCapture, setMessageCapture] = useState<string | null>(null);
    const [tri, setTri] = useState<{ cle: CleTri; croissant: boolean }>({
        cle: 'societe_symbole',
        croissant: true,
    });

    const changerTri = useCallback((cle: CleTri) => {
        setTri((actuel) =>
            actuel.cle === cle
                ? { cle, croissant: !actuel.croissant }
                : { cle, croissant: COLONNES_TEXTE.has(cle) }
        );
    }, []);

    const ouvrirSerie = useCallback(
        async (symbole: string) => {
            setSelection(symbole);
            setSerie(null);
            setSerie(await chargerSerie(symbole));
        },
        [chargerSerie]
    );

    // Garde la courbe en phase avec le rafraîchissement automatique de la liste.
    useEffect(() => {
        if (!selection || !run) return;
        let annule = false;
        chargerSerie(selection).then((s) => {
            if (!annule) setSerie(s);
        });
        return () => {
            annule = true;
        };
    }, [run, selection, chargerSerie]);

    const lancerCapture = useCallback(async () => {
        const statut = await capturer();
        setMessageCapture(
            statut === 'PERIME'
                ? 'La BRVM n’a pas rafraîchi sa page depuis le dernier relevé — aucune observation ajoutée (comportement normal entre deux publications).'
                : statut === 'OK'
                ? 'Nouveau relevé enregistré.'
                : statut === 'ERREUR'
                ? 'La page BRVM n’a pas pu être lue. Voir le journal des passages.'
                : null
        );
    }, [capturer]);

    const lignes = useMemo(() => {
        const valeur = (l: LiveCours): string | number | null => {
            const env = ohlc[l.societe_symbole];
            switch (tri.cle) {
                case 'plus_haut_observe':
                    return env?.plus_haut_observe ?? null;
                case 'plus_bas_observe':
                    return env?.plus_bas_observe ?? null;
                case 'nb_observations':
                    return env?.nb_observations ?? 0;
                default:
                    return l[tri.cle];
            }
        };
        const sens = tri.croissant ? 1 : -1;
        return [...cours].sort((a, b) => {
            const va = valeur(a);
            const vb = valeur(b);
            // Une valeur absente (pas d'échange, pas d'extrême) reste en fin de
            // liste quel que soit le sens : ce n'est ni un minimum ni un maximum.
            if (va === null && vb === null) return a.societe_symbole.localeCompare(b.societe_symbole);
            if (va === null) return 1;
            if (vb === null) return -1;
            const ecart =
                typeof va === 'string' || typeof vb === 'string'
                    ? String(va).localeCompare(String(vb), 'fr')
                    : va - vb;
            return ecart !== 0 ? ecart * sens : a.societe_symbole.localeCompare(b.societe_symbole);
        });
    }, [cours, ohlc, tri]);

    const pointsGraphe = useMemo(
        () =>
            (serie?.points ?? [])
                .filter((p) => p.a_traite && p.cours_dernier !== null)
                .map((p) => ({ heure: fmtHeure(p.releve_at), cours: p.cours_dernier })),
        [serie]
    );

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box>
                    <p style={{ padding: '20px' }}>Accès refusé. Permission admin_platform requise.</p>
                </Box>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <Header icon='speed' title='Marché Live BRVM' />

            <div style={{ display: 'flex', gap: '8px', margin: '0 0 14px' }}>
                <button
                    className={onglet === 'seance' ? 'button button-purple' : 'button'}
                    onClick={() => setOnglet('seance')}
                >
                    Séance en cours
                </button>
                <button
                    className={onglet === 'periode' ? 'button button-purple' : 'button'}
                    onClick={() => setOnglet('periode')}
                >
                    Sur une période
                </button>
            </div>

            {onglet === 'periode' && <LiveMarketPeriode />}

            {onglet === 'seance' && (
            <>
            {/* Fraîcheur : l'information la plus importante de l'écran. */}
            <Box>
                <div
                    className='box-title box-vertical-padding box-horizontal-padding'
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                        <p style={{ margin: 0 }}>
                            Dernière mise à jour BRVM : <strong>{fmtHorodatage(run?.releve_at ?? null)}</strong>
                        </p>
                        {meta && run && (
                            // Hors séance, un relevé ancien est l'état normal : il n'est
                            // signalé « périmé » que si la BRVM est censée publier.
                            meta.perime && !meta.publication_attendue ? (
                                <span style={STYLE_BADGE.NEUTRE}>DERNIÈRE SÉANCE CAPTÉE</span>
                            ) : (
                                <span style={meta.perime ? STYLE_BADGE.PERIME : STYLE_BADGE.OK}>
                                    {meta.perime ? 'PÉRIMÉ' : 'À JOUR'}
                                    {meta.age_minutes != null && ` · ${fmtDuree(Math.round(meta.age_minutes))}`}
                                </span>
                            )
                        )}
                        {meta?.dernier_essai && meta.dernier_essai.id !== run?.id && (
                            // Distingue « la BRVM ne publie plus » de « notre minuteur est mort ».
                            <span style={{ fontSize: '12px', color: '#64748b' }}>
                                dernier passage : {fmtHeure(meta.dernier_essai.capture_at)} ({meta.dernier_essai.statut})
                            </span>
                        )}
                    </div>
                    <button className='button button-purple' onClick={lancerCapture} disabled={captureEnCours}>
                        <i className='material-icons' style={{ fontSize: '18px', marginRight: '5px' }}>refresh</i>
                        {captureEnCours ? 'Capture…' : 'Rafraîchir maintenant'}
                    </button>
                </div>

                <div className='box-content box-horizontal-padding' style={{ padding: '0 20px 20px' }}>
                    {meta?.seance && <BandeauSeance seance={meta.seance} />}

                    {/* ⚠️ Avertissement non négociable : ces données ne sont pas le BOC. */}
                    <div
                        style={{
                            padding: '12px 16px',
                            borderRadius: '8px',
                            backgroundColor: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            color: '#1e40af',
                            fontSize: '13px',
                            lineHeight: 1.5,
                        }}
                    >
                        <strong>Données échantillonnées, non officielles.</strong> Relevé toutes les 15 minutes sur la page
                        publique de la BRVM, qui ne publie ni plus haut ni plus bas. Les extrêmes affichés sont ceux
                        <em> observés aux instants de capture</em> : ce sont des minorants de l'amplitude réelle. La colonne
                        « Obs. » indique sur combien de relevés chaque extrême repose. La référence de fin de séance reste le
                        bulletin officiel de la cote.
                    </div>

                    {messageCapture && (
                        <p style={{ marginTop: '12px', fontSize: '13px', color: '#475569' }}>{messageCapture}</p>
                    )}
                    {error && (
                        <p style={{ marginTop: '12px', fontSize: '13px', color: COULEUR_BAISSE }}>{error}</p>
                    )}
                </div>
            </Box>

            {/* Activité du marché — captée sur la même page, sans requête supplémentaire. */}
            {activite && (
                <Box>
                    <div className='box-title box-vertical-padding box-horizontal-padding'>
                        <p>Activité du marché</p>
                    </div>
                    <div
                        className='box-content box-horizontal-padding'
                        style={{ padding: '0 20px 20px', display: 'flex', gap: '28px', flexWrap: 'wrap' }}
                    >
                        {activite.indices.map((idx) => (
                            <div key={idx.code}>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>{idx.libelle}</div>
                                <div style={{ fontSize: '20px', fontWeight: 700 }}>{fmtNombre(idx.valeur, 2)}</div>
                                <div
                                    style={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: (idx.variation_pct ?? 0) >= 0 ? COULEUR_HAUSSE : COULEUR_BAISSE,
                                    }}
                                >
                                    {(idx.variation_pct ?? 0) >= 0 ? '+' : ''}
                                    {fmtNombre(idx.variation_pct, 2)} %
                                </div>
                            </div>
                        ))}
                        <div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Valeur des transactions</div>
                            <div style={{ fontSize: '20px', fontWeight: 700 }}>
                                {fmtMontantCourt(activite.valeur_transactions)}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Capitalisation actions</div>
                            <div style={{ fontSize: '20px', fontWeight: 700 }}>
                                {fmtMontantCourt(activite.capitalisation_actions)}
                            </div>
                        </div>
                        <div>
                            <div style={{ fontSize: '12px', color: '#64748b' }}>Capitalisation obligations</div>
                            <div style={{ fontSize: '20px', fontWeight: 700 }}>
                                {fmtMontantCourt(activite.capitalisation_obligations)}
                            </div>
                        </div>
                    </div>
                </Box>
            )}

            {/* Courbe intraday de la valeur sélectionnée. */}
            {selection && (
                <Box>
                    <div
                        className='box-title box-vertical-padding box-horizontal-padding'
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <p>
                            {selection}
                            {serie?.nom_affiche ? ` — ${serie.nom_affiche}` : ''}
                            {serie ? ` · ${serie.nb_points} relevé(s)` : ''}
                        </p>
                        <button className='button' onClick={() => { setSelection(null); setSerie(null); }}>
                            Fermer
                        </button>
                    </div>
                    <div className='box-content box-horizontal-padding' style={{ padding: '0 20px 20px' }}>
                        {pointsGraphe.length === 0 ? (
                            <p style={{ color: '#94a3b8' }}>
                                Aucun échange observé sur cette valeur pendant la séance.
                            </p>
                        ) : (
                            <ResponsiveContainer width='100%' height={260}>
                                <LineChart data={pointsGraphe} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                                    <CartesianGrid strokeDasharray='3 3' stroke='#e2e8f0' />
                                    <XAxis dataKey='heure' tick={{ fontSize: 12 }} />
                                    <YAxis domain={['auto', 'auto']} tick={{ fontSize: 12 }} width={70} />
                                    <Tooltip formatter={(v: number) => `${fmtNombre(v)} F`} />
                                    {serie?.plus_haut_observe !== null && serie?.plus_haut_observe !== undefined && (
                                        <ReferenceLine
                                            y={serie.plus_haut_observe}
                                            stroke={COULEUR_HAUSSE}
                                            strokeDasharray='4 4'
                                            label={{ value: 'plus haut observé', position: 'insideTopRight', fontSize: 11 }}
                                        />
                                    )}
                                    {serie?.plus_bas_observe !== null && serie?.plus_bas_observe !== undefined && (
                                        <ReferenceLine
                                            y={serie.plus_bas_observe}
                                            stroke={COULEUR_BAISSE}
                                            strokeDasharray='4 4'
                                            label={{ value: 'plus bas observé', position: 'insideBottomRight', fontSize: 11 }}
                                        />
                                    )}
                                    <Line
                                        type='stepAfter'
                                        dataKey='cours'
                                        stroke='#764ba2'
                                        strokeWidth={2}
                                        dot={{ r: 3 }}
                                        isAnimationActive={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                        <p style={{ marginTop: '10px', fontSize: '12px', color: '#64748b' }}>
                            Courbe en escalier : entre deux relevés, le cours réel est inconnu. Les points sont les seuls
                            instants observés.
                        </p>
                    </div>
                </Box>
            )}

            {/* Tableau des cours. */}
            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>
                        Cours de la séance
                        {meta && ` · ${meta.nb_valeurs_traitees}/${meta.nb_valeurs} valeur(s) ayant traité`}
                    </p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '0 20px 20px' }}>
                    {loading ? (
                        <p>Chargement du marché live…</p>
                    ) : lignes.length === 0 ? (
                        <p style={{ color: '#94a3b8', padding: '20px 0' }}>
                            Aucun relevé en base. Le flux démarre au prochain passage planifié, ou via « Rafraîchir
                            maintenant ».
                        </p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        {COLONNES.map((c) => {
                                            const actif = tri.cle === c.cle;
                                            return (
                                                <th
                                                    key={c.cle}
                                                    onClick={() => changerTri(c.cle)}
                                                    aria-sort={actif ? (tri.croissant ? 'ascending' : 'descending') : 'none'}
                                                    title='Trier'
                                                    style={{ ...(c.gauche ? thGauche : th), cursor: 'pointer', userSelect: 'none' }}
                                                >
                                                    {c.libelle}
                                                    <span style={{ marginLeft: '4px', opacity: actif ? 1 : 0.35 }}>
                                                        {actif ? (tri.croissant ? '▲' : '▼') : '↕'}
                                                    </span>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {lignes.map((l, i) => {
                                        const env = ohlc[l.societe_symbole];
                                        const variation = l.variation_pct ?? 0;
                                        return (
                                            <tr
                                                key={l.societe_symbole}
                                                onClick={() => ouvrirSerie(l.societe_symbole)}
                                                style={{
                                                    backgroundColor:
                                                        selection === l.societe_symbole
                                                            ? '#ede9fe'
                                                            : i % 2 === 0
                                                            ? 'transparent'
                                                            : 'var(--bg-hover)',
                                                    cursor: 'pointer',
                                                    // Une valeur qui n'a pas traité porte un cours reporté,
                                                    // pas un prix : on l'estompe au lieu de la présenter
                                                    // comme une cotation du jour.
                                                    opacity: l.a_traite ? 1 : 0.55,
                                                }}
                                            >
                                                <td style={{ ...tdGauche, fontWeight: 600 }}>{l.societe_symbole}</td>
                                                <td style={{ ...tdGauche, color: '#64748b' }}>{l.nom_affiche ?? '—'}</td>
                                                <td style={td}>{fmtNombre(l.volume)}</td>
                                                <td style={td}>{fmtNombre(l.cours_veille)}</td>
                                                <td style={td}>{fmtNombre(l.cours_ouverture)}</td>
                                                <td style={{ ...td, fontWeight: 600 }}>{fmtNombre(l.cours_dernier)}</td>
                                                <td
                                                    style={{
                                                        ...td,
                                                        fontWeight: 600,
                                                        color: variation >= 0 ? COULEUR_HAUSSE : COULEUR_BAISSE,
                                                    }}
                                                >
                                                    {variation >= 0 ? '+' : ''}
                                                    {fmtNombre(l.variation_pct, 2)} %
                                                </td>
                                                <td style={td}>{fmtNombre(env?.plus_haut_observe ?? null)}</td>
                                                <td style={td}>{fmtNombre(env?.plus_bas_observe ?? null)}</td>
                                                <td style={{ ...td, color: '#64748b' }}>{env?.nb_observations ?? 0}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Box>

            {/* Journal des passages : la preuve d'exécution du flux. */}
            <Box>
                <div
                    className='box-title box-vertical-padding box-horizontal-padding'
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => setJournalOuvert((v) => !v)}
                >
                    <p>Journal des passages ({runs.length})</p>
                    <i className='material-icons'>{journalOuvert ? 'expand_less' : 'expand_more'}</i>
                </div>
                {journalOuvert && (
                    <div className='box-content box-horizontal-padding' style={{ padding: '0 20px 20px' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th style={thGauche}>Appel</th>
                                        <th style={thGauche}>Horodatage BRVM</th>
                                        <th style={thGauche}>Statut</th>
                                        <th style={th}>Lignes</th>
                                        <th style={th}>Durée</th>
                                        <th style={thGauche}>Détail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.map((r, i) => (
                                        <tr
                                            key={r.id}
                                            style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}
                                        >
                                            <td style={tdGauche}>{fmtHeure(r.capture_at)}</td>
                                            <td style={tdGauche}>{fmtHeure(r.releve_at)}</td>
                                            <td style={tdGauche}>
                                                <span style={STYLE_BADGE[r.statut] ?? STYLE_BADGE.ERREUR}>{r.statut}</span>
                                            </td>
                                            <td style={td}>{r.nb_lignes}</td>
                                            <td style={td}>{r.duree_ms === null ? '—' : `${r.duree_ms} ms`}</td>
                                            <td style={{ ...tdGauche, color: '#64748b', fontSize: '12px' }}>
                                                {r.message ?? ''}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Box>
            </>
            )}
        </SiteLayout>
    );
};

export default AdminLiveMarketScreen;
